import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '../../../lib/supabase';
import { fetchAllTasks } from '../../../lib/supabaseTasks';
import { pushUndo } from '../../../lib/undoStack';
import { recurrencePayloadFields, recurrenceStateFromRow } from '../../../lib/recurrenceState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Shared hook for task editing logic.
 * Used by PlannerApp and Dashboard to open/save the EditTaskDialog.
 */
export function useTaskEdit({ tasks, setTasks, userId, createSharedItem, deleteSharedItem, subtasksApi }) {
  const [showEditTask, setShowEditTask] = useState(false);
  const [editingTaskData, setEditingTaskData] = useState(null);

  const handleEditTask = (task) => {
    const parentId = task.parent_task_id || task.id;
    const parent = tasks.find(t => t.id === parentId);
    const isRecurring = !!(task.is_recurring || task.parent_task_id);

    setEditingTaskData({
      id: task.id,
      title: task.title,
      description: task.description || '',
      time: task.time || '',
      duration_minutes: task.duration_minutes ?? null,
      priority: task.priority || 'none',
      date: task.date,
      isRecurring: isRecurring,
      // The ten recurrence* dialog keys, incl. the monthly sentinel decoding.
      ...recurrenceStateFromRow(parent || task),
      originalIsRecurring: isRecurring,
      originalParentTaskId: task.parent_task_id,
      originalRecurrenceType: (parent || task).recurrence_type || 'weekly',
      originalRecurrenceInterval: (parent || task).recurrence_interval || 1,
      // R34: assign-after-create (see updateTask 'assign')
      assigned_to: null,
      assigned_to_name: '',
      completed: !!task.completed,
      isRecurringParent: !!task.is_recurring && !task.parent_task_id, // same rule as lib/recurringTasks.js
    });
    setShowEditTask(true);
  };

  const updateTask = async (scope = 'single') => {
    if (!editingTaskData?.title.trim()) return;
    try {
      const wasRecurring = editingTaskData.originalIsRecurring || !!editingTaskData.originalParentTaskId;
      const nowRecurring = editingTaskData.isRecurring;

      // Case A (R34, 2026-09-07): assign an existing personal task. Personal
      // tasks live in Supabase, assigned tasks are Mongo shared items, so the
      // task MOVES: create the shared item FIRST (server validates the
      // assignee is a church member and notifies them), delete the Supabase
      // row SECOND. The worst failure is a harmless duplicate, never a lost
      // task — the same order convert-to-recurring uses.
      if (scope === 'assign') {
        if (!editingTaskData.assigned_to) return;
        if (typeof createSharedItem !== 'function') throw new Error('Assigning is not available here');
        if (editingTaskData.completed) throw new Error('Completed tasks cannot be assigned');
        if (editingTaskData.isRecurringParent) throw new Error('Stop the repeat first, then assign this task');
        const row = tasks.find(t => t.id === editingTaskData.id);
        const st = subtasksApi?.getForTask?.(row || { id: editingTaskData.id }) || { items: [], checkedIds: [] };
        const item = await createSharedItem({
          item_type: 'task',
          title: editingTaskData.title,
          description: editingTaskData.description || '',
          assigned_to: editingTaskData.assigned_to,
          due_date: editingTaskData.date,
          priority_level: editingTaskData.priority || 'none',
          subtasks: st.items.map(s => ({ id: s.id, title: s.title, completed: st.checkedIds.includes(s.id) })),
        });
        const { error: delErr } = await supabase.from('tasks').delete().eq('id', editingTaskData.id);
        if (delErr) {
          console.error('Assigned, but the personal copy could not be removed:', delErr);
          toast.warning(`Assigned to ${editingTaskData.assigned_to_name || 'teammate'}, but the original task is still on your list — delete it by hand.`);
        } else {
          setTasks(prev => prev.filter(t => t.id !== editingTaskData.id));
          const assigneeName = editingTaskData.assigned_to_name || 'teammate';
          if (row) {
            const { isOverdue, originalDate, ...clean } = row; // strip view-only fields
            pushUndo(`"${row.title}" assigned to ${assigneeName}`, async () => {
              if (typeof deleteSharedItem === 'function') {
                const removed = await deleteSharedItem(item);
                if (removed === false) throw new Error('shared item delete failed'); // no duplicate: the personal row is not re-inserted
              }
              const { data, error } = await supabase.from('tasks').insert([clean]).select().single();
              if (error) throw error;
              if (data) setTasks(prev => [...prev.filter(t => t.id !== data.id), data]);
            }, { toastMessage: `Task assigned to ${assigneeName}` });
          } else {
            toast.success(`Task assigned to ${assigneeName}`);
          }
        }
        setShowEditTask(false);
        setEditingTaskData(null);
        return;
      }

      // Case 0: Recurring task toggled OFF — stop recurrence
      if (wasRecurring && !nowRecurring && scope === 'stop_recurring') {
        const response = await fetch(`${API_URL}/api/planner/tasks/update-recurring`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            task_id: editingTaskData.id,
            user_id: userId,
            scope: 'stop_recurring',
            title: editingTaskData.title,
            description: editingTaskData.description || null,
            date: editingTaskData.date,
            time: editingTaskData.time || null,
            duration_minutes: editingTaskData.time ? (editingTaskData.duration_minutes ?? -1) : -1,
            priority: editingTaskData.priority,
          })
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.detail || 'Failed to stop recurring task');
        const { data: freshTasks, error } = await fetchAllTasks(supabase, userId);
        if (!error && freshTasks) {
          setTasks(freshTasks);
        }
        setShowEditTask(false);
        setEditingTaskData(null);
        return;
      }

      // Case 1: Non-recurring task being converted to recurring
      if (!wasRecurring && nowRecurring && scope === 'convert_to_recurring') {
        // Create the recurring series FIRST. Only delete the original on
        // success — swapping delete-before-create (previous order) risked
        // permanent data loss on network failure.
        const response = await fetch(`${API_URL}/api/planner/tasks/create-recurring`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userId,
            title: editingTaskData.title,
            description: editingTaskData.description || null,
            date: editingTaskData.date,
            time: editingTaskData.time || null,
            duration_minutes: editingTaskData.time ? (editingTaskData.duration_minutes || null) : null,
            priority: editingTaskData.priority,
            ...recurrencePayloadFields(editingTaskData),
          })
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.detail || 'Failed to create recurring task');

        // Series created successfully — now safe to delete the original.
        // If THIS fails, user sees a harmless duplicate (recoverable) instead
        // of losing the original task entirely.
        await supabase.from('tasks').delete().eq('id', editingTaskData.id);

        const allNewTasks = [result.parent, ...(result.instances || [])];
        setTasks(prev => [...prev.filter(t => t.id !== editingTaskData.id), ...allNewTasks]);
        setShowEditTask(false);
        setEditingTaskData(null);
        return;
      }

      // Case 2: Recurring task being edited — use scope (single/future/all)
      if (wasRecurring && (scope === 'single' || scope === 'future' || scope === 'all')) {
        const response = await fetch(`${API_URL}/api/planner/tasks/update-recurring`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            task_id: editingTaskData.id,
            user_id: userId,
            scope: scope,
            title: editingTaskData.title,
            description: editingTaskData.description || null,
            date: editingTaskData.date,
            time: editingTaskData.time || null,
            duration_minutes: editingTaskData.time ? (editingTaskData.duration_minutes ?? -1) : -1,
            priority: editingTaskData.priority,
            ...recurrencePayloadFields(editingTaskData),
          })
        });
        const result = await response.json();
        if (!response.ok || !result.success) throw new Error(result.detail || 'Failed to update recurring task');

        if (scope === 'single') {
          const updated = result.updated_tasks?.[0];
          if (updated) {
            setTasks(prev => prev.map(t => t.id === updated.id ? updated : t));
          }
        } else {
          const { data: freshTasks, error } = await fetchAllTasks(supabase, userId);
          if (!error && freshTasks) {
            setTasks(freshTasks);
          }
        }
        setShowEditTask(false);
        setEditingTaskData(null);
        return;
      }

      // Case 3: Non-recurring task, simple update
      const updatePayload = {
        title: editingTaskData.title,
        description: editingTaskData.description,
        time: editingTaskData.time || null,
        priority: editingTaskData.priority,
        date: editingTaskData.date
      };
      // Only include duration_minutes when we have a Time (and therefore a meaningful duration)
      // OR explicitly clearing it. Skip if both time and duration are absent to stay
      // compatible with schemas pre-migration.
      if (editingTaskData.time) {
        updatePayload.duration_minutes = editingTaskData.duration_minutes || null;
      }
      const { error } = await supabase.from('tasks').update(updatePayload).eq('id', editingTaskData.id);
      if (error) throw error;
      setTasks(prev => prev.map(t => t.id === editingTaskData.id ? {
        ...t,
        title: editingTaskData.title,
        description: editingTaskData.description,
        time: editingTaskData.time || null,
        duration_minutes: editingTaskData.time ? (editingTaskData.duration_minutes || null) : null,
        priority: editingTaskData.priority,
        date: editingTaskData.date,
      } : t));
      setShowEditTask(false);
      setEditingTaskData(null);
    } catch (err) {
      console.error('Error updating task:', err);
      toast.error(err?.message || "Couldn't save the task. Please try again.");
    }
  };

  return {
    showEditTask, setShowEditTask,
    editingTaskData, setEditingTaskData,
    handleEditTask,
    updateTask
  };
}
